import React from "react";
import { ConfigProvider, Spin } from "antd";
import { decryptText, encryptText } from "./Utils/Encryption";
import updateUserStates, {
  getRefreshToken,
  handleLogout,
} from "./Utils/UpdateUsersState";
import { refresh } from "./API/auth";
import axios from "axios";
import { createContext, useEffect, useState } from "react";
import AppRoutes from "./Routes/AppRoutes";
import "./App.css";
import { SocketProvider, useSocket } from "./Utils/Context/SocketContext";
import { DateFilterProvider } from "./Contexts/DateFilterContext";

const UserContext = createContext();
function App() {
  const [userData, setUserData] = useState({
    role: "",
    id: "",
    email: "",
    name: "",
    phone: "",
    profile_picture: "",
    permissions: ""
  });
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)

  const [userPages, setUserPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userToken, setUserToken] = useState({ access: "", refresh: "" });
  const refreshAccessToken = async (token) => {
    try {
      let myRefreshToken = token?.refresh;
      if (!myRefreshToken) {
        myRefreshToken = getRefreshToken();
      }

      // Call backend to refresh token
      const res = await refresh(myRefreshToken);

      // If refresh successful
      if (res?.status === 200) {
        const localStorageItems = localStorage;

        // Remove old encrypted token from localStorage
        if (localStorageItems.length !== 0) {
          for (let key in localStorageItems) {
            if (localStorageItems[key] && decryptText(key) === "token") {
              localStorage.removeItem(key);
            }
          }
        }

        // Save new tokens (encrypted)
        localStorage.setItem(
          encryptText("token"),
          encryptText(
            JSON.stringify({
              access: res?.data?.access,
              refresh: res?.data?.refresh,
            }),
          ),
        );

        // Update React states
        updateUserStates(setUserData, setUserToken);

        return res?.data?.access;
      } else {
        // If refresh token invalid or failed
        handleLogout(setUserData, setUserToken);
        window.location.href = "/login";
        return null;
      }
    } catch (error) {
      // If any error occurs during refresh
      handleLogout(setUserData, setUserToken);
      window.location.href = "/login";
      return null;
    }
  };

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (
        error?.response?.status === 401 &&
        error.config.url !== `${process.env.REACT_APP_BACKEND_URL}token/` &&
        error.config.url !==
        `${process.env.REACT_APP_BACKEND_URL}token/refresh/`
      ) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          error.config.headers["Authorization"] = "Bearer " + newToken;
          return axios(error.config);
        } else {
          handleLogout(setUserData, setUserToken);
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    },
  );

  useEffect(() => {
    const initializeUserState = async () => {
      setLoading(true);
      const updatedStates = updateUserStates(setUserData, setUserToken);
      if (
        updatedStates?.token?.access &&
        updatedStates?.token?.refresh &&
        loading
      ) {
        await refreshAccessToken(updatedStates.token);
      }
      setLoading(false);
    };

    if (
      userData.role === "" &&
      userToken.access === "" &&
      userToken.refresh === "" &&
      loading
    ) {
      initializeUserState();
    } else {
      setLoading(false);
    }

  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#15528A",
        },
        components: {
          Select: {
            optionSelectedBg: "#15528A10",
            optionSelectedColor: "#15528A",
            optionActiveBg: "#15528A08",
          },
        },
      }}
    >
      <div className="App">
        <UserContext.Provider
          value={{
            userData,
            setUserData,
            userToken,
            setUserToken,
            userPages,
            setUserPages,
            unreadNotificationCount,
            setUnreadNotificationCount
          }}
        >
          <SocketProvider>
            <DateFilterProvider>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    width: "100%",
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <AppRoutes />
              )}
            </DateFilterProvider>
          </SocketProvider>
        </UserContext.Provider>
      </div>
    </ConfigProvider >
  );
}

export default App;
export { UserContext };
